import {
  hasPersistedCancerDetectContext,
  hasPersistedPatientContext,
  restartFullStory,
  shutdownFullStory,
} from '@/utils/fullstory';
import { useIsPatientContext } from '@hooks/context/useContextStore';
import { useCancerDetectUser } from '@hooks/useCancerDetectUser';
import { useLayoutEffect } from 'react';

/**
 * Shuts down FullStory when the active experience is PRO (patient/partner-org)
 * or CancerDetect UI, so those sessions are never recorded. Restarts otherwise
 * (consumer wellness, or logged out) so tracking still works for the next
 * non-restricted session on a shared browser.
 *
 * Gated on context flags alone (not also on a logged-in user) — those flags
 * only become true through authenticated flows, and user-store / context-store
 * rehydrate independently, so requiring both risked a window where context
 * resolves first and the effect incorrectly takes the restart branch.
 *
 * Also OR'd with the synchronous persisted-storage checks: zustand's persist
 * rehydration is asynchronous even for sessionStorage/localStorage, so the
 * very first effect run can see both live flags still at their default
 * `false` before rehydration completes. Without the persisted check that run
 * would call restartFullStory(), which queues right behind the early
 * shutdownFullStory() main.tsx already issued for a persisted restricted
 * session and undoes it once FullStory's command queue is processed.
 */
export const useFullStorySync = () => {
  const isPatientContext = useIsPatientContext();
  const isCancerDetectClicked = useCancerDetectUser(
    (s) => s.isCancerDetectClicked,
  );

  useLayoutEffect(() => {
    const isRestricted =
      isPatientContext ||
      isCancerDetectClicked ||
      hasPersistedPatientContext() ||
      hasPersistedCancerDetectContext();

    if (isRestricted) {
      shutdownFullStory();
    } else {
      restartFullStory();
    }
  }, [isPatientContext, isCancerDetectClicked]);
};
